<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, useAttrs, watch} from 'vue'
import {useI18n} from 'vue-i18n'

const {t} = useI18n()

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  time: {
    type: Number,
    required: true,
  },
  maxTime: {
    type: Number,
    required: false,
    default: 1800,
  },
  active: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const cap = computed(() => (isMobile.value ? 70 : 92))
const daireCevre = computed(() => 2 * Math.PI * cap.value)

const attrs = useAttrs()
const providedMax = computed(() => Math.max(0, Math.floor(Number(props.maxTime) || 0)))
const fallbackMax = ref(0)

watch(
  () => providedMax.value,
  (next) => {
    if (next > 0) {
      fallbackMax.value = next
    }
  },
  { immediate: true },
)

watch(
  () => Number(props.time) || 0,
  (next) => {
    if (providedMax.value > 0) {
      return
    }

    if (next > fallbackMax.value) {
      fallbackMax.value = Math.floor(next)
    } else if (fallbackMax.value === 0 && next > 0) {
      fallbackMax.value = Math.floor(next)
    }
  },
  { immediate: true },
)

const maxSeconds = computed(() => Math.max(1, fallbackMax.value))
const kalanSure = computed(() => Math.max(0, Math.floor(Number(props.time) || 0)))

const progress = computed(() => Math.min(1, Math.max(0, kalanSure.value / maxSeconds.value)))

const dashOffset = computed(() => daireCevre.value * (1 - progress.value))

const formattedTime = computed(() => {
  const totalSeconds = kalanSure.value
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
})

const bgColorClass = computed(() => (props.active ? 'stroke-blue-100' : 'stroke-gray-100'))
const progressColorClass = computed(() => (props.active ? 'stroke-blue-600' : 'stroke-gray-400'))

// Announce time: every 10 seconds, last 10 seconds every 5 seconds, last 5 seconds every second
const lastAnnouncedTime = ref(-1)
const announcedTimeText = ref('')

watch(
  kalanSure,
  (currentTime) => {
    const shouldAnnounce =
      currentTime <= 5 || // Last 5 seconds: every second
      (currentTime <= 10 && currentTime % 5 === 0) || // 6-10 seconds: every 5 seconds
      currentTime % 10 === 0 || // Normal: every 10 seconds
      lastAnnouncedTime.value === -1 // Initial announcement

    if (shouldAnnounce && currentTime !== lastAnnouncedTime.value) {
      lastAnnouncedTime.value = currentTime
      announcedTimeText.value = t('accessibility.remaining_time', {time: formattedTime.value})
    }
  },
  {immediate: true}
)

const strokeWidth = computed(() => (isMobile.value ? 20 : 4))

const svgSize = computed(() => cap.value * 2 + strokeWidth.value)
const center = computed(() => svgSize.value / 2)
const viewBox = computed(() => `0 0 ${svgSize.value} ${svgSize.value}`)
</script>

<template>
  <div
    class="w-full h-full flex items-center justify-end md:justify-center"
    v-bind="attrs"
    role="timer"
  >
    <div class="inline-flex items-center gap-1 md:relative md:w-full md:h-full">
      <svg
        class="timer-ring shrink-0 w-8 h-8 md:w-full md:h-full"
        :viewBox="viewBox"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          class="timer-ring__bg"
          :class="bgColorClass"
          :cx="center"
          :cy="center"
          :r="cap"
          :stroke-width="strokeWidth"
        />
        <circle
          class="timer-ring__progress"
          :class="progressColorClass"
          :cx="center"
          :cy="center"
          :r="cap"
          :stroke-dasharray="daireCevre"
          :stroke-dashoffset="dashOffset"
          :stroke-width="strokeWidth"
        />
      </svg>
      <div
        class="flex items-center justify-end min-w-[80px] md:min-w-0 md:w-full md:absolute md:inset-0 md:justify-center"
      >
        <span
          class="text-display-sm text-center tabular-nums font-mono"
          :class="active ? 'text-blue-600' : 'text-gray-400'"
          aria-hidden="true"
          >{{ formattedTime }}</span
        >
        <span class="sr-only" aria-live="polite" aria-atomic="true">{{ announcedTimeText }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.timer-ring {
  transform: rotate(-90deg);
}

.timer-ring__bg {
  fill: none;
}

.timer-ring__progress {
  fill: none;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.85s cubic-bezier(0.4, 0, 0.2, 1);
}

.timer-ring__bg.stroke-gray-100 {
  stroke: #f3f4f6;
}

.timer-ring__bg.stroke-blue-100 {
  stroke: #dbeafe;
}

.timer-ring__progress.stroke-gray-400 {
  stroke: #9ca3af;
}

.timer-ring__progress.stroke-blue-600 {
  stroke: #2563eb;
}
</style>
